/**
 * 🧩 To add a new sidebar item:
 * 1. Add a new object to the appropriate array (analyticsItems, adminItems, etc.).
 * 2. Use `getFeatureStatus(featureFlag)` to control visibility & locking. (featureFlag from license)
 * 3. Use `Auth.hasFeaturePermission("feature_key")` for permission checks.
 * 4. If needed, group into `accordionItems` for sub-navigation.
 * 5. All items default to `shouldRender: true` and `isLocked: false` unless specified.
 */
import { FC } from "react"
import { useLocation } from "react-router-dom"

// icons
import CameraIcon from "@mui/icons-material/CameraAltOutlined"
import LicenseIcon from "@mui/icons-material/CardMembership"
import CategoryIcon from "@mui/icons-material/Category"
import CarsIcon from "@mui/icons-material/Commute"
import MCPIcon from "@mui/icons-material/CompareArrows"
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun"
import MotionIcon from "@mui/icons-material/DirectionsRunOutlined"
import DefectDetectionAnalyticsIcon from "@mui/icons-material/DisabledByDefaultOutlined"
import DefectDetectionSetupIcon from "@mui/icons-material/DisabledByDefaultOutlined"
import NodesIcon from "@mui/icons-material/DnsOutlined"
import IntrusionIcon from "@mui/icons-material/DoNotStep"
import MarketingIcon from "@mui/icons-material/Equalizer"
import OccupancyIcon from "@mui/icons-material/EventSeatOutlined"
import SecurityEventsIcon from "@mui/icons-material/GridOn"
import APIIntegrationIcon from "@mui/icons-material/IntegrationInstructionsOutlined"
import HeatmapIcon from "@mui/icons-material/LensBlur"
import FloorIcon from "@mui/icons-material/Map"
import PlaceIcon from "@mui/icons-material/Place"
import ConnectivityIcon from "@mui/icons-material/SettingsInputHdmiOutlined"
import CameraLive from "@mui/icons-material/VideocamOutlined"
import RolesIcon from "@mui/icons-material/VpnKeyOutlined"
import { Paper } from "@mui/material"

import { Typography } from "@synapse-analytics/synapse-ui"

import FaceRecognitionIcon from "../../../assets/faceRecognitionIcon.svg?react"
import { routes } from "../../../routes/routes"
import { useFeaturesStore } from "../../../store/featuresStore"
import { AccordionSidebarItemProps, BaseSidebarItemProps } from "../../../types/Custom/Interfaces"
import { Persona } from "../../../types/Custom/Types"
import Auth from "../../../utils/auth"
import { isMultipleBranchesMode } from "../helpers"
import SidebarItem from "./SidebarItem"

import styles from "./UserData.module.scss"

/** Props for the UserData component */
interface Props {
  /** The persona type (analytics, admin, marketing, security, industrial) */
  persona: Persona
  /** The currently active screen */
  activeScreen: string
}

/** Union type for sidebar item props */
type SidebarItemProps = BaseSidebarItemProps | AccordionSidebarItemProps | string

/** Interface for feature status */
interface FeatureStatus {
  /** A flag indicating whether the item should render */
  shouldRender: boolean
  /** A flag indicating whether the item is locked */
  isLocked?: boolean
}

/**
 * Gets the feature status based on the feature value
 * @param featureValue - The feature value to check
 * @returns The feature status object
 */
const getFeatureStatus = (featureValue?: boolean | null): FeatureStatus => {
  // eslint-disable-next-line eqeqeq
  if (featureValue == undefined) {
    return { shouldRender: false, isLocked: false }
  }
  return { shouldRender: true, isLocked: !featureValue }
}

/**
 * UserData component that renders different sidebar items based on persona
 * @param props - The component props
 * @returns The rendered component
 */
const UserData: FC<Props> = ({ activeScreen, persona }: Props): JSX.Element => {
  const location = useLocation()
  const availableFeatures = useFeaturesStore((s) => s.features)
  const isMultipleBranchesRoute = isMultipleBranchesMode(location.pathname)

  const analyticsItems: SidebarItemProps[] = [
    {
      label: "People Count",
      Icon: DirectionsRunIcon,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      ...getFeatureStatus(availableFeatures?.analytics?.counter),
      accordionItems: [
        {
          label: "Gates",
          route: routes?.gates,
          subRoutes: [routes?.liveGates, routes?.historyGates],
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Categories",
          route: routes?.categories,
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Tenants",
          route: routes?.tenantsList,
          subRoutes: [routes?.tenantDetails],
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Dwelling Areas",
          route: routes?.dwellingAreas,
          subRoutes: [routes?.dwellingAreaDetails],
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Checkout Counters",
          route: routes?.checkoutCounter,
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Corridors",
          route: routes?.corridors,
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Customer Service",
          route: routes?.customerService,
          hasPermission: Auth.hasFeaturePermission("people_count"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Missing Data",
          route: routes?.missingData,
          hasPermission: Auth.hasFeaturePermission("missing_data"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
      ].filter((item) => item.shouldRender !== false),
    },
    {
      label: "Occupancy",
      subRoutes: [routes?.staffOccupancy, routes?.seatOccupancy],
      Icon: OccupancyIcon,
      hasPermission: Auth.hasFeaturePermission("zones_occupancy"),
      ...getFeatureStatus(availableFeatures?.analytics?.occupancy),
      accordionItems: [
        {
          label: "Staff Occupancy",
          route: routes?.staffOccupancy,
          hasPermission: Auth.hasFeaturePermission("zones_occupancy"),
          ...getFeatureStatus(availableFeatures?.analytics?.occupancy),
        },
        {
          label: "Seat Occupancy",
          route: routes?.seatOccupancy,
          hasPermission: Auth.hasFeaturePermission("zones_occupancy"),
          ...getFeatureStatus(availableFeatures?.analytics?.occupancy),
        },
      ].filter((item) => item.shouldRender !== false),
    },
    {
      label: "Heatmap",
      subRoutes: [routes?.heatmap, routes?.oldHeatmap],
      Icon: HeatmapIcon,
      hasPermission: Auth.hasFeaturePermission("heatmap"),
      ...getFeatureStatus(availableFeatures?.analytics?.heatmap),
      accordionItems: [
        {
          label: "Heatmap V2",
          route: routes?.heatmap,
          hasPermission: Auth.hasFeaturePermission("heatmap"),
          ...getFeatureStatus(availableFeatures?.analytics?.heatmap),
        },
        {
          label: "Heatmap",
          route: routes?.oldHeatmap,
          tag: { label: "deprecated", color: "yellow" },
          hasPermission: Auth.hasFeaturePermission("heatmap"),
          ...getFeatureStatus(availableFeatures?.analytics?.heatmap),
        },
      ].filter((item) => item.shouldRender !== false),
    },
    {
      label: "MCP",
      subRoutes: [routes?.floorsMCP, routes?.camerasMCP],
      Icon: MCPIcon,
      hasPermission: Auth.hasFeaturePermission("mcp"),
      ...getFeatureStatus(availableFeatures?.analytics?.mcp),
      accordionItems: [
        {
          label: "Floors MCP",
          route: routes?.floorsMCP,
          hasPermission: Auth.hasFeaturePermission("mcp"),
          ...getFeatureStatus(availableFeatures?.analytics?.mcp),
        },
        {
          label: "Cameras MCP",
          route: routes?.camerasMCP,
          hasPermission: Auth.hasFeaturePermission("mcp"),
          ...getFeatureStatus(availableFeatures?.analytics?.mcp),
        },
      ].filter((item) => item.shouldRender !== false),
    },
    {
      label: "Cars",
      Icon: CarsIcon,
      hasPermission: Auth.hasFeaturePermission("cars_analytics"),
      ...getFeatureStatus(availableFeatures?.analytics?.cars),
      accordionItems: [
        {
          label: "Cars Analytics",
          route: routes?.carsAnalytics,
          hasPermission: Auth.hasFeaturePermission("cars_analytics"),
          ...getFeatureStatus(availableFeatures?.analytics?.cars),
        },
      ].filter((item) => item.shouldRender !== false),
    },
  ].filter((item) => typeof item === "string" || item.shouldRender !== false)

  // Sidebar items for multiple branches mode - use the exported function for consistency
  const multipleBranchesItems = [
    {
      label: "People Count",
      Icon: DirectionsRunIcon,
      hasPermission: Auth.hasFeaturePermission("people_count") && Auth.hasFeaturePermission("multiple_branches"),
      ...getFeatureStatus(availableFeatures?.analytics?.counter),
      accordionItems: [
        {
          label: "Gates",
          route: routes?.branchesGatesAnalytics,
          hasPermission: Auth.hasFeaturePermission("people_count") && Auth.hasFeaturePermission("multiple_branches"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
        {
          label: "Categories",
          route: routes?.branchesCategories,
          hasPermission: Auth.hasFeaturePermission("categories") && Auth.hasFeaturePermission("multiple_branches"),
          ...getFeatureStatus(availableFeatures?.analytics?.counter),
        },
      ].filter((item) => item.shouldRender !== false),
    },
    {
      label: "Connectivity Check",
      Icon: ConnectivityIcon,
      hasPermission:
        Auth.hasFeaturePermission("cameras") &&
        Auth.hasFeaturePermission("nodes") &&
        Auth.hasFeaturePermission("multiple_branches"),
      shouldRender: true,
      accordionItems: [
        {
          label: "Cameras",
          route: routes?.branchesCamerasConnectivity,
          hasPermission: Auth.hasFeaturePermission("cameras") && Auth.hasFeaturePermission("multiple_branches"),
        },
        {
          label: "Nodes",
          route: routes?.branchesNodesConnectivity,
          hasPermission: Auth.hasFeaturePermission("nodes") && Auth.hasFeaturePermission("multiple_branches"),
        },
      ],
    },
  ].filter((item) => typeof item === "string" || item.shouldRender !== false)

  const adminItems: SidebarItemProps[] = [
    {
      label: "Cameras",
      Icon: CameraIcon,
      hasPermission: Auth.hasFeaturePermission("cameras"),
      accordionItems: [
        {
          label: "Cameras List",
          route: routes?.cameraList,
          subRoutes: [routes?.cameraDetails],
          hasPermission: Auth.hasFeaturePermission("cameras"),
        },
        {
          label: "Cameras View Changes",
          route: routes?.cameraViewChanges,
          hasPermission: Auth.hasFeaturePermission("cameras"),
        },
      ],
    },
    {
      label: "Floors Setup",
      route: `${routes?.floors}`,
      Icon: FloorIcon,
      hasPermission: Auth.hasFeaturePermission("floor"),
      shouldRender: !!availableFeatures?.analytics.mcp || !!availableFeatures?.analytics.heatmap,
    },
    {
      label: "Entities & Regions",
      route: `${routes?.entitiesAndRegions}`,
      Icon: PlaceIcon,
      hasPermission: Auth.hasFeaturePermission("entities"),
    },
    {
      label: "Intrusion Setup",
      route: `${routes?.intrusionSetup}`,
      Icon: IntrusionIcon,
      hasPermission: Auth.hasFeaturePermission("intrusion_setup"),
      shouldRender: !!availableFeatures?.security?.intrusion,
    },
    {
      label: "Motion Setup",
      route: `${routes?.motionSetup}`,
      Icon: MotionIcon,
      hasPermission: Auth.hasFeaturePermission("motion_setup"),
      shouldRender: !!availableFeatures?.security?.motion,
    },
    {
      label: "Nodes",
      route: `${routes?.nodesList}`,
      subRoutes: [routes?.nodeDownAnalytics],
      Icon: NodesIcon,
      hasPermission: Auth.hasFeaturePermission("nodes"),
    },
    "Generic Items",
    {
      label: "Categories Management",
      route: `${routes?.categoriesList}`,
      Icon: CategoryIcon,
      hasPermission: Auth.hasFeaturePermission("categories"),
    },
    {
      label: "Users & Permissions",
      route: `${routes?.users}`,
      subRoutes: [routes?.createUser, routes?.userDetails],
      Icon: RolesIcon,
      hasPermission: Auth.hasFeaturePermission("users"),
    },
    {
      label: "Cars Lists",
      route: `${routes?.carsList}`,
      Icon: CarsIcon,
      hasPermission: Auth.hasFeaturePermission("cars_admin"),
    },
    {
      label: "API Integration",
      route: `${routes?.apiIntegration}`,
      Icon: APIIntegrationIcon,
      hasPermission: Auth.hasFeaturePermission("api_integration"),
    },
    {
      label: "License",
      route: `${routes?.license}`,
      Icon: LicenseIcon,
      hasPermission: true,
    },
    "Industrial Items",
    {
      label: "Defect Detection Setup",
      route: `${routes?.defectDetectionSetup}`,
      Icon: DefectDetectionSetupIcon,
      hasPermission: Auth.hasFeaturePermission("defect_detection_setup"),
    },
  ].filter((item) => typeof item === "string" || item.shouldRender !== false)

  const marketingItems: SidebarItemProps[] = [
    {
      label: "Campaign List",
      route: `${routes?.campaign}`,
      Icon: MarketingIcon,
      hasPermission: Auth.hasFeaturePermission("campaign"),
      ...getFeatureStatus(availableFeatures?.marketing.campaign),
    },
  ].filter((item) => typeof item === "string" || item.shouldRender !== false)

  const securityItems: SidebarItemProps[] = [
    {
      label: "Live Streams",
      route: `${routes?.liveStreams}`,
      Icon: CameraLive,
      hasPermission: Auth.hasFeaturePermission("security_camera_stream"),
    },
    {
      label: "Incidents",
      route: `${routes?.securityIncidents}`,
      Icon: SecurityEventsIcon,
      hasPermission: Auth.hasFeaturePermission("security_events"),
    },
    {
      label: "Car Logs",
      route: `${routes?.carsLogs}`,
      Icon: CarsIcon,
      hasPermission: Auth.hasFeaturePermission("cars_security"),
      ...getFeatureStatus(availableFeatures?.security?.cars),
    },
    {
      label: "Face Recognition",
      route: `${routes?.faceRecognition}`,
      subRoutes: [`${routes?.profileVisits}`],
      Icon: FaceRecognitionIcon,
      hasPermission: Auth.hasFeaturePermission("facial_recognition"),
      ...getFeatureStatus(availableFeatures?.security?.face),
    },
  ].filter((item: SidebarItemProps) => typeof item === "string" || item.shouldRender !== false)

  const industrialItems: SidebarItemProps[] = [
    {
      label: "Defect Detection",
      route: `${routes?.defectDetection}`,
      Icon: DefectDetectionAnalyticsIcon,
      hasPermission: Auth.hasFeaturePermission("defect_detection"),
      ...getFeatureStatus(availableFeatures?.industrial?.defect_detection),
    },
  ].filter((item: SidebarItemProps) => typeof item === "string" || item.shouldRender !== false)

  const renderSidebarItems = (items: SidebarItemProps[]): JSX.Element[] =>
    items.map((item, index) =>
      typeof item === "string" ? (
        <Typography key={`${item}-${index}`} variant="span-bold" variantColor={2}>
          {item}
        </Typography>
      ) : (
        <SidebarItem key={`${item.label}-${index}`} activeScreen={activeScreen} {...item} />
      )
    )

  // Determine which sidebar items to render based on mode and persona
  const personaItemsMap: Partial<Record<Persona, SidebarItemProps[]>> = {
    analytics: analyticsItems,
    admin: adminItems,
    marketing: marketingItems,
    security: securityItems,
    industrial: industrialItems,
  }

  const sidebarItems = isMultipleBranchesRoute ? multipleBranchesItems : personaItemsMap[persona] ?? []

  return (
    <Paper className={styles.userData} data-testid="user-data">
      <Typography
        variant="span-bold"
        textTransform="capitalize"
        variantColor={2}
        gutterBottom
        className={styles.descTitle}
      >
        {isMultipleBranchesRoute ? "Multiple Branches" : `${persona} details`}
      </Typography>
      <div className={styles.itemsWrapper}>{renderSidebarItems(sidebarItems)}</div>
    </Paper>
  )
}

export default UserData
